import { Group } from "@mantine/core"
import { IconEdit, IconTrash } from "@tabler/icons-react"
import { createColumnHelper } from "@tanstack/react-table"
import { ActionIcon, CountryCell, TextCell } from "@tfg/ui"
import { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"

import { FindProductionSitesForSupplierResponseDto } from "~/gen/api"
import { COUNTRIES } from "~/utils/countries"
import { formatAddress } from "~/utils/format-address"
import { useGetOperations } from "~/utils/operations"

const columnHelper =
  createColumnHelper<FindProductionSitesForSupplierResponseDto>()

function formatRegistrationIds(
  rows: FindProductionSitesForSupplierResponseDto["registrationIds"] | undefined
): string {
  if (rows === undefined || rows.length === 0) return ""
  return rows.map((r) => r.registrationId).join(", ")
}

function formatPerformedActivities(
  rows:
    | FindProductionSitesForSupplierResponseDto["performedActivities"]
    | undefined,
  getActivityNameByCode: (activityCode: string) => string
): string {
  if (rows === undefined || rows.length === 0) return ""
  return rows.map((a) => getActivityNameByCode(a.name)).join(", ")
}

export type UseSupplierProductionSitesColumnsParams = {
  isAdmin: boolean
  onEdit: (site: FindProductionSitesForSupplierResponseDto) => void
  onDelete: (site: FindProductionSitesForSupplierResponseDto) => void
}

export const useSupplierProductionSitesColumns = ({
  isAdmin,
  onEdit,
  onDelete,
}: UseSupplierProductionSitesColumnsParams) => {
  const { t } = useTranslation("supplier")
  const operations = useGetOperations()

  const getActivityNameByCode = useCallback(
    (activityCode: string): string =>
      operations[activityCode]?.name ?? activityCode,
    [operations]
  )

  return useMemo(() => {
    const cols = []

    if (isAdmin) {
      cols.push(
        columnHelper.accessor("id", {
          id: "id",
          enableSorting: false,
          header: () => (
            <TextCell
              fw="var(--table-header-text-weight)"
              value={t("productionSites.table.headers.id")}
            />
          ),
          cell: (info) => <TextCell value={info.getValue()} c="violet" />,
        })
      )
      cols.push(
        columnHelper.display({
          id: "deleted",
          enableSorting: false,
          header: () => (
            <TextCell
              fw="var(--table-header-text-weight)"
              value={t("productionSites.table.headers.deleted")}
            />
          ),
          cell: ({ row }) =>
            row.original.deletedAt ? (
              <span style={{ color: "var(--mantine-color-red-6)" }}>●</span>
            ) : null,
        })
      )
    }

    cols.push(
      columnHelper.accessor("name", {
        id: "name",
        enablePinning: true,
        enableSorting: false,
        header: () => (
          <TextCell
            fw="var(--table-header-text-weight)"
            value={t("productionSites.table.headers.name")}
            tooltip={t("productionSiteModal.tooltips.name")}
          />
        ),
        cell: (info) => <TextCell value={info.getValue()} />,
      }),
      columnHelper.accessor((row) => row.parentCompany.name, {
        id: "parentCompany",
        enableSorting: false,
        header: () => (
          <TextCell
            fw="var(--table-header-text-weight)"
            value={t("productionSites.table.headers.parentCompany")}
            tooltip={t("productionSiteModal.tooltips.parentCompany")}
          />
        ),
        cell: (info) => <TextCell value={info.getValue()} />,
      }),
      columnHelper.display({
        id: "address",
        enableSorting: false,
        header: () => (
          <TextCell
            fw="var(--table-header-text-weight)"
            value={t("productionSites.table.headers.address")}
            tooltip={t("productionSiteModal.tooltips.address")}
          />
        ),
        cell: ({ row }) => (
          <CountryCell
            countryIso3166={row.original.country}
            w="100%"
            tooltip={formatAddress(row.original)}
            countriesMapper={COUNTRIES}
          />
        ),
      }),
      columnHelper.accessor("internalCode", {
        id: "internalCode",
        enableSorting: false,
        header: () => (
          <TextCell
            fw="var(--table-header-text-weight)"
            value={t("productionSites.table.headers.internalCode")}
            tooltip={t("productionSiteModal.tooltips.internalCode")}
          />
        ),
        cell: (info) => <TextCell value={info.getValue() ?? ""} />,
      }),
      columnHelper.accessor("registrationIds", {
        id: "registrationIds",
        enableSorting: false,
        header: () => (
          <TextCell
            fw="var(--table-header-text-weight)"
            value={t("productionSites.table.headers.registrationId")}
            tooltip={t("productionSiteModal.tooltips.registrationId")}
          />
        ),
        cell: (info) => (
          <TextCell value={formatRegistrationIds(info.getValue())} />
        ),
      }),
      columnHelper.accessor("performedActivities", {
        id: "performedActivities",
        enableSorting: false,
        header: () => (
          <TextCell
            fw="var(--table-header-text-weight)"
            value={t("productionSites.table.headers.performedActivities")}
            tooltip={t("productionSiteModal.tooltips.performedActivities")}
          />
        ),
        cell: (info) => (
          <TextCell
            value={formatPerformedActivities(
              info.getValue(),
              getActivityNameByCode
            )}
          />
        ),
      }),
      columnHelper.accessor("certificates", {
        id: "certificates",
        enableSorting: false,
        header: () => (
          <TextCell
            fw="var(--table-header-text-weight)"
            value={t("productionSites.table.headers.certificates")}
          />
        ),
        cell: (info) => {
          const certs = info.getValue()
          if (certs.length === 0) return <TextCell value="" />
          return <TextCell value={certs.map((c) => c.type).join(", ")} />
        },
      }),
      columnHelper.display({
        id: "actions",
        header: undefined,
        cell: ({ row }) => {
          const site = row.original
          if (site.deletedAt !== null) return null
          return (
            <Group wrap="nowrap">
              <ActionIcon icon={<IconEdit />} onClick={() => onEdit(site)} />
              <ActionIcon
                variant="secondary-critical"
                color="red"
                icon={<IconTrash />}
                onClick={() => onDelete(site)}
              />
            </Group>
          )
        },
      })
    )

    return cols
  }, [t, isAdmin, onEdit, onDelete, getActivityNameByCode])
}
